import { useEffect, useRef, useState } from "react";
import { Play, Square, Check, Music2, X } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Chips } from "@/components/editor/stickers/StickerOptions";
import { FavStar } from "@/components/editor/stickers/FavStar";
import { PassagePicker } from "@/components/editor/stickers/PassagePicker";
import { MusicPresets } from "@/components/editor/stickers/MusicPresets";

export const musicUrl = (id) => `${API}/fx/music/${id}.mp3`;

const Vol = ({ label, value, onChange, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="text-[11px] text-slate-400 w-24 shrink-0">{label}</span>
    <Slider value={[value * 100]} min={0} max={150} step={5} onValueChange={([v]) => onChange(v / 100)} />
    <span className="text-[11px] font-mono text-white w-10 text-right">{Math.round(value * 100)}%</span>
  </div>
);

const Fade = ({ label, value, onChange, testid }) => (
  <div className="flex items-center gap-3" data-testid={testid}>
    <span className="text-[11px] text-slate-400 w-24 shrink-0">{label}</span>
    <Slider value={[value * 10]} min={0} max={50} step={1} onValueChange={([v]) => onChange(v / 10)} />
    <span className="text-[11px] font-mono text-white w-10 text-right">{value.toFixed(1)}s</span>
  </div>
);

export const MusicPanel = ({ cat, cfg, setCfg, favs, toggleFav, len = 15 }) => {
  const { t, lang } = useI18n();
  const [c, setC] = useState("all");
  const [playing, setPlaying] = useState(null);
  const audio = useRef(null);
  useEffect(() => () => audio.current?.pause(), []);
  const toggle = (id) => {
    audio.current?.pause();
    if (playing === id) return setPlaying(null);
    audio.current = new Audio(musicUrl(id));
    audio.current.volume = 0.8;
    audio.current.play().catch(() => {});
    audio.current.onended = () => setPlaying(null);
    setPlaying(id);
  };
  const music = cfg.music;
  const pick = (id) => {
    const long = ((cat.music || []).find((m) => m.id === id)?.dur || 0) >= 30;
    setCfg((x) => ({ ...x, music: x.music?.id === id ? null : { id, vol: x.music?.vol ?? 0.8, orig_vol: x.music?.orig_vol ?? 0.6, sync: !long, shift: 0, start: 0,
      fade_in: x.music?.fade_in ?? 0.3, fade_out: x.music?.fade_out ?? 1.2, duck: x.music?.duck ?? true } }));
  };
  const setM = (patch) => setCfg((x) => ({ ...x, music: { ...x.music, ...patch } }));
  const list = (cat.music || []).filter((m) => c === "all" || (c === "fav" ? favs.music.includes(m.id) : m.cat === c));
  const name = (m) => (lang === "fr" ? m.fr : m.en);
  const current = (cat.music || []).find((m) => m.id === music?.id);
  return (
    <div className="space-y-2.5" data-testid="music-panel">
      <MusicPresets cat={cat} music={music} setCfg={setCfg} />
      {current && (
        <div className="rounded-lg border border-[#3ad7ff]/40 bg-[#2f7bff]/10 p-3 space-y-2" data-testid="music-selected">
          <div className="flex items-center gap-2 text-xs text-white">
            <Music2 className="w-4 h-4 text-[#3ad7ff]" /><span className="flex-1 truncate" data-testid="music-selected-name">{name(current)}</span>
            <button type="button" onClick={() => setCfg((x) => ({ ...x, music: null }))} data-testid="music-remove-btn" className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <Vol label={t("mu.vol")} value={music.vol ?? 0.8} onChange={(v) => setM({ vol: v })} testid="music-vol" />
          <Vol label={t("mu.orig_vol")} value={music.orig_vol ?? 0.6} onChange={(v) => setM({ orig_vol: v })} testid="music-orig-vol" />
          <Fade label={t("mu.fade_in")} value={music.fade_in ?? 0.3} onChange={(v) => setM({ fade_in: v })} testid="music-fade-in" />
          <Fade label={t("mu.fade_out")} value={music.fade_out ?? 1.2} onChange={(v) => setM({ fade_out: v })} testid="music-fade-out" />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] text-slate-400">{t("mu.duck")}</span>
            <button type="button" onClick={() => setM({ duck: !music.duck })} data-testid="music-duck-toggle" aria-pressed={!!music.duck}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold ${music.duck ? "cx-metal cx-metal-active" : "cx-metal"}`}>{music.duck ? t("mu.sync_on") : t("mu.sync_off")}</button>
          </div>
          <div className="grid grid-cols-2 gap-1 rounded-lg bg-black/30 p-1 mt-1" data-testid="music-mode">
            {[[true, "mu.mode_drop"], [false, "mu.mode_passage"]].map(([v, k]) => (
              <button key={k} type="button" onClick={() => setM({ sync: v })} data-testid={`music-mode-${v ? "drop" : "passage"}`} aria-pressed={(music.sync ?? true) === v}
                className={`rounded-md py-1.5 text-[11px] font-semibold transition-colors ${(music.sync ?? true) === v ? "bg-[#a746ff]/25 text-white shadow-[inset_0_0_0_1px_rgba(167,70,255,0.5)]" : "text-slate-500 hover:text-slate-300"}`}>{t(k)}</button>
            ))}
          </div>
          {!(music.sync ?? true) && <PassagePicker id={music.id} dur={current.dur} len={len} start={music.start ?? 0} onChange={(v) => setM({ start: v })} />}
          {(music.sync ?? true) && (
            <div className="space-y-1" data-testid="music-shift-control">
              <div className="flex items-center gap-3">
                <span className="text-[11px] text-slate-400 w-24 shrink-0">{t("mu.shift")}</span>
                <Slider value={[(music.shift ?? 0) * 10]} min={-60} max={60} step={1} onValueChange={([v]) => setM({ shift: v / 10 })} />
                <span className="text-[11px] font-mono text-white w-12 text-right" data-testid="music-shift-value">{(music.shift ?? 0) > 0 ? "+" : ""}{(music.shift ?? 0).toFixed(1)}s</span>
              </div>
              <p className="text-[10px] text-slate-500" data-testid="music-sync-hint">{t(cfg.impact != null ? "mu.sync_hint" : "mu.sync_hint_auto").replace("{t}", cfg.impact != null ? Number(cfg.impact).toFixed(1) : "")}</p>
            </div>
          )}
        </div>
      )}
      <Chips testid="music-cat" items={["fav", "all", ...(cat.music_cats || [])]} value={c} onChange={setC} label={(k) => (k === "fav" ? `★ ${t("fav.label")} (${favs.music.length})` : t(`mu.cat_${k}`))} />
      {c === "fav" && !list.length && <p className="text-[11px] text-slate-500 py-3" data-testid="music-fav-empty">{t("fav.empty")}</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-64 overflow-y-auto cx-scroll pr-1" data-testid="music-grid">
        {list.map((m) => (
          <div key={m.id} className={`flex items-center rounded-lg border overflow-hidden transition-colors ${music?.id === m.id ? "border-[#a746ff]/70 bg-[#a746ff]/10" : playing === m.id ? "border-[#3ad7ff]/70 bg-[#3ad7ff]/10" : "border-white/10 bg-white/[0.02]"}`}>
            <button type="button" data-testid={`music-play-${m.id}`} onClick={() => toggle(m.id)} className="px-2.5 py-2 text-slate-400 hover:text-[#3ad7ff]">
              {playing === m.id ? <Square className="w-3.5 h-3.5 fill-current" /> : <Play className="w-3.5 h-3.5 fill-current" />}
            </button>
            <span className="flex-1 min-w-0 truncate text-xs text-slate-200" title={name(m)}>{name(m)}</span>
            <span className="text-[10px] font-mono text-slate-500 mr-1">{m.dur}s</span>
            <FavStar on={favs.music.includes(m.id)} onClick={() => toggleFav("music", m.id)} testid={`music-fav-${m.id}`} />
            <button type="button" data-testid={`music-${m.id}`} onClick={() => pick(m.id)} className="px-2.5 py-2 border-l border-white/10 text-slate-400 hover:text-white hover:bg-[#2f7bff]/20">
              {music?.id === m.id ? <Check className="w-3.5 h-3.5 text-[#c79bff]" /> : <Music2 className="w-3.5 h-3.5" />}
            </button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500">{t("mu.note")}</p>
    </div>
  );
};
